import { useState, useEffect, useRef } from "react";

export function AnimatedCounter({ value, prefix = "", suffix = "", duration = 1200, decimals = 0, className = "" }) {
  const [displayValue, setDisplayValue] = useState(0);
  const prevValue = useRef(0);
  const animationRef = useRef(null);
  const startTime = useRef(null);

  useEffect(() => {
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
    if (isNaN(numValue)) return;

    const from = prevValue.current;
    const to = numValue;
    
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    const animate = (timestamp) => {
      if (!startTime.current) startTime.current = timestamp;
      const elapsed = timestamp - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + (to - from) * eased;
      
      setDisplayValue(current);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        prevValue.current = to;
        startTime.current = null;
      }
    };

    startTime.current = null;
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [value, duration]);

  const formatted = decimals > 0 
    ? displayValue.toFixed(decimals) 
    : Math.round(displayValue).toLocaleString();

  return (
    <span className={className} data-testid="animated-counter">
      {prefix}{formatted}{suffix}
    </span>
  );
}
